import { getDomain } from "tldts"
import { validateEmail } from "@/lib/forms/validation"

/**
 * Consumer-mail domains where deriving a "what's your website?" default from
 * the email is nonsensical. Non-exhaustive — extend as new freemail providers
 * surface in real campaign data.
 */
const FREEMAIL_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com", "googlemail.com",
  "yahoo.com", "yahoo.co.uk", "ymail.com",
  "hotmail.com", "outlook.com", "live.com", "msn.com",
  "icloud.com", "me.com", "mac.com",
  "aol.com",
  "proton.me", "protonmail.com", "pm.me",
  "gmx.com", "gmx.net",
  "mail.com",
  "yandex.com", "yandex.ru",
  "zoho.com",
  "fastmail.com",
])

/**
 * Given an email address, return a `https://www.<registrable-domain>` URL
 * suitable for pre-populating a "what's your website?" field on an audit
 * landing page. Returns `null` for invalid emails, freemail providers,
 * localhost, IPs, and any input where `tldts.getDomain` cannot resolve a
 * registrable domain.
 */
export function deriveUrlFromEmail(email: string | null | undefined): string | null {
  if (!email) return null
  const trimmed = email.trim()
  if (!validateEmail(trimmed)) return null

  const rawDomain = trimmed.slice(trimmed.lastIndexOf("@") + 1).toLowerCase()

  const registrable = getDomain(rawDomain)
  if (!registrable) return null

  if (FREEMAIL_DOMAINS.has(registrable)) return null

  return `https://www.${registrable}`
}
